import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import AddIndicatorForm from '../components/AddIndicatorForm';
import AddRelationshipForm from '../components/AddRelationshipForm';
import DeleteButton from '../components/DeleteButton';
import LinkExistingEntity from '../components/LinkExistingEntity';
import EntityDetailLayout from '../components/EntityDetailLayout';
import EntityPivotLink from '../components/EntityPivotLink';
import InvestigationGraph from '../components/InvestigationGraph';
import RelationshipTable from '../components/RelationshipTable';
import useEvidenceTimezone from '../hooks/useEvidenceTimezone';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'indicators', label: 'Indicators' },
  { id: 'raw', label: 'Raw' },
];

function ActorDetail() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const { format, suffix } = useEvidenceTimezone();
  const [activeTab, setActiveTab] = useState('overview');
  const [actor, setActor] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [graphOpen, setGraphOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [relRefreshKey, setRelRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [actorRes, iocRes] = await Promise.all([
        api.get(`/hdp/actors/${actorId}`),
        api.get(`/hdp/iocs?actor_id=${actorId}`),
      ]);
      setActor(actorRes.data);
      setIndicators(iocRes.data.iocs || []);
    } catch (err) {
      console.error(err);
      setActor(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [actorId]);

  const startEditing = () => {
    if (!actor) return;
    setEditForm({
      display_name: actor.display_name || '',
      actor_type: actor.actor_type || 'threat_actor',
      status: actor.status || 'active',
      risk_level: actor.risk_level || 'medium',
      summary: actor.summary || '',
      known_aliases: (actor.known_aliases || []).join(', '),
      attribution_notes: actor.attribution_notes || '',
      case_id: actor.case_id || '',
    });
    setEditError('');
    setEditing(true);
  };

  const saveActorEdit = async (event) => {
    event.preventDefault();
    if (!editForm.display_name?.trim()) return;
    setSavingEdit(true);
    setEditError('');
    try {
      const aliases = editForm.known_aliases
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const res = await api.patch(`/hdp/actors/${actorId}`, {
        display_name: editForm.display_name.trim(),
        actor_type: editForm.actor_type,
        status: editForm.status,
        risk_level: editForm.risk_level,
        summary: editForm.summary,
        known_aliases: aliases,
        attribution_notes: editForm.attribution_notes,
        case_id: editForm.case_id.trim() || null,
      });
      setActor(res.data);
      setEditing(false);
      setRelRefreshKey((key) => key + 1);
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Failed to save actor');
    } finally {
      setSavingEdit(false);
    }
  };

  const sidebar = actor ? (
    <>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Quick actions</div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGraphOpen(true)}>
          Investigation graph
        </button>
      </div>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Linked case</div>
        {actor.case_id ? (
          <EntityPivotLink type="case" id={actor.case_id} label={actor.case_title || actor.case_id} />
        ) : (
          <span className="entity-sidebar-muted">No linked case</span>
        )}
      </div>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Risk</div>
        <span className={`risk-pill risk-${actor.risk_level}`}>{actor.risk_level}</span>
      </div>
    </>
  ) : null;

  return (
    <>
      <EntityDetailLayout
        entityType="actor"
        title={actor?.display_name || 'Threat actor'}
        subtitle={actor?.actor_type}
        breadcrumbs={[
          { label: 'Dashboard', to: '/' },
          { label: 'Threat Actors', to: '/actors' },
          { label: actor?.display_name || actorId },
        ]}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        loading={loading}
        notFound={!loading && !actor ? 'Threat actor not found.' : null}
        notFoundAction={<Link to="/actors" className="btn btn-secondary btn-sm">Back to actors</Link>}
        headerActions={actor && (
          <DeleteButton
            confirmMessage={`Delete actor "${actor.display_name}"?`}
            onDelete={async () => {
              await api.delete(`/hdp/actors/${actorId}`);
              navigate('/actors');
            }}
          />
        )}
        sidebar={sidebar}
      >
        {activeTab === 'overview' && actor && (
          <>
            {!editing && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={startEditing}>
                  Edit actor
                </button>
              </div>
            )}

            {editing ? (
              <form onSubmit={saveActorEdit}>
                <div className="form-group">
                  <label className="form-label">Display name</label>
                  <input
                    className="form-input"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, display_name: e.target.value }))}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      className="form-input"
                      value={editForm.actor_type}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, actor_type: e.target.value }))}
                    >
                      <option value="threat_actor">threat_actor</option>
                      <option value="intrusion_set">intrusion_set</option>
                      <option value="campaign">campaign</option>
                      <option value="malware_author">malware_author</option>
                      <option value="unknown">unknown</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-input"
                      value={editForm.status}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="active">active</option>
                      <option value="dormant">dormant</option>
                      <option value="disrupted">disrupted</option>
                      <option value="unknown">unknown</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Risk</label>
                    <select
                      className="form-input"
                      value={editForm.risk_level}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, risk_level: e.target.value }))}
                    >
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                      <option value="critical">critical</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Linked case ID</label>
                  <input
                    className="form-input"
                    value={editForm.case_id}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, case_id: e.target.value }))}
                    placeholder="Optional case UUID"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Summary</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={editForm.summary}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, summary: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Known aliases (comma-separated)</label>
                  <input
                    className="form-input"
                    value={editForm.known_aliases}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, known_aliases: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Attribution notes</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={editForm.attribution_notes}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, attribution_notes: e.target.value }))}
                  />
                </div>
                {editError && <p style={{ color: 'var(--danger, #f66)' }}>{editError}</p>}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn btn-primary" disabled={savingEdit}>
                    {savingEdit ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={savingEdit}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="entity-kv-grid">
                <div><span>Status</span><strong>{actor.status}</strong></div>
                <div><span>Confidence</span><strong>{actor.confidence_score}</strong></div>
                <div><span>First seen ({suffix})</span><strong>{format(actor.first_seen)}</strong></div>
                <div><span>Last seen ({suffix})</span><strong>{format(actor.last_seen)}</strong></div>
                <div className="entity-kv-wide">
                  <span>Summary</span>
                  <p>{actor.summary || '—'}</p>
                </div>
                <div className="entity-kv-wide">
                  <span>Known aliases</span>
                  <div className="entity-tag-list">
                    {(actor.known_aliases || []).length === 0 ? (
                      <span className="entity-sidebar-muted">—</span>
                    ) : (
                      (actor.known_aliases || []).map((alias) => (
                        <span key={alias} className="entity-tag">{alias}</span>
                      ))
                    )}
                  </div>
                </div>
                <div className="entity-kv-wide">
                  <span>Attribution notes</span>
                  <p>{actor.attribution_notes || '—'}</p>
                </div>
              </div>
            )}
          </>
        )}
        {activeTab === 'relationships' && actor && (
          <>
            <AddRelationshipForm
              sourceType="actor"
              sourceId={actorId}
              sourceLabel={actor.display_name}
              allowedTargetTypes={['indicator', 'search', 'evidence', 'actor', 'case']}
              actorId={actorId}
              caseId={actor.case_id || undefined}
              onCreated={() => {
                setRelRefreshKey((key) => key + 1);
                // Refresh indicators tab too — inline create attaches actor_id
                api.get(`/hdp/iocs?actor_id=${actorId}`).then((res) => {
                  setIndicators(res.data.iocs || []);
                }).catch(() => {});
              }}
            />
            <RelationshipTable
              entityType="actor"
              entityId={actorId}
              refreshKey={relRefreshKey}
            />
          </>
        )}
        {activeTab === 'indicators' && actor && (
          <>
            <div className="entity-attach-panel">
              <LinkExistingEntity
                mode="indicator-to-actor"
                actorId={actorId}
                caseId={actor.case_id || undefined}
                onLinked={(ioc) => {
                  setIndicators((prev) => {
                    if (prev.some((row) => row.indicator_id === ioc.indicator_id)) return prev;
                    return [ioc, ...prev];
                  });
                  setRelRefreshKey((key) => key + 1);
                }}
              />
              <div className="entity-attach-divider">or create new</div>
              <AddIndicatorForm
                actorId={actorId}
                caseId={actor.case_id || undefined}
                onCreated={(ioc) => {
                  setIndicators((prev) => [ioc, ...prev]);
                  setRelRefreshKey((key) => key + 1);
                }}
              />
            </div>
            <table className="data-table entity-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Confidence</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {indicators.length === 0 ? (
                  <tr><td colSpan={4}>No linked indicators.</td></tr>
                ) : indicators.map((item) => (
                  <tr key={item.indicator_id}>
                    <td>{item.ioc_type}</td>
                    <td>
                      <EntityPivotLink type="indicator" id={item.indicator_id} label={item.value} showBadge={false} />
                    </td>
                    <td>{item.confidence}</td>
                    <td>
                      <DeleteButton
                        confirmMessage={`Delete indicator "${item.value}"?`}
                        onDelete={async () => {
                          await api.delete(`/hdp/iocs/${item.indicator_id}`);
                          setIndicators((prev) => prev.filter((row) => row.indicator_id !== item.indicator_id));
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {activeTab === 'raw' && actor && (
          <pre className="entity-raw-json">{JSON.stringify(actor, null, 2)}</pre>
        )}
      </EntityDetailLayout>

      {graphOpen && actor && (
        <InvestigationGraph
          entityType="actor"
          entityId={actorId}
          onClose={() => setGraphOpen(false)}
        />
      )}
    </>
  );
}

export default ActorDetail;
